import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ConfigProvider, Layout } from "antd";
import { Urls } from "../../config/const";
import Header from "./Header";

import { layout } from "../../config/testIds";

import styled from "styled-components";

import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import SignIn from "../Authentication/SignIn";
import MainTabs from "../MainTabs/MainTabs";

import imgBg from "../../images/layout/dekra-bg.jpg";
import Notifications from "./Notifications";
import Footer from "./Footer";
import { UIProvider } from "../../context/ui-context";
import InactivityLogoutTimer from "./InactivityLogoutTimer";

import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { Locale } from "antd/lib/locale";

import dayjs from "dayjs";

import "dayjs/locale/it";
import "dayjs/locale/en-gb";

import locale_IT from "antd/locale/it_IT";
import locale_EN from "antd/locale/en_GB";
import ApplicationErrorsModal from "./ApplicationErrorsModal";

const LayoutStyled = styled(Layout)`
  height: 100vh;
  z-index: 1;

  .ant-layout {
    background: #fff;
  }
`;

const ContentStyled = styled(Layout.Content)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
`;

const InnerContent = styled.div`
  min-width: 100vw;
  overflow: auto;
  height: calc(100vh - 80px);
  background-image: url(${imgBg});
  background-size: cover;
`;

type Props = {
  children?: JSX.Element | JSX.Element[];
};

type LocaleInfoType = {
  locale: Locale;
  dayjsLocale: string;
};

const locales: Record<string, LocaleInfoType> = {
  it: { locale: locale_IT, dayjsLocale: "it" },
  en: { locale: locale_EN, dayjsLocale: "en-gb" },
};

const AppLayout: React.FC<Props> = (props: Props) => {
  const { children } = props;

  const [locale, setLocale] = useState<Locale>(locale_IT);

  const { language } = useSelector((state: RootState) => state.user);

  useEffect(() => {
    let selectedLocale = locales[language];

    if (!selectedLocale) selectedLocale = locales["it"];

    setLocale(selectedLocale.locale);
    dayjs.locale(selectedLocale.dayjsLocale);
  }, [language]);

  return (
    <ConfigProvider
      locale={locale}
      theme={{
        hashed: false,
        token: {
          fontFamily: "Rubik, sans-serif",
        },
      }}
    >
      <LayoutStyled data-testid={layout.container}>
        <AuthenticatedTemplate>
          <UIProvider>
            <BrowserRouter
              future={{
                v7_startTransition: true, // Enables React's startTransition API
                v7_relativeSplatPath: true, // Enables relative paths in nested routes
              }}
            >
              <Layout>
                <InactivityLogoutTimer />

                <Header />

                <InnerContent>
                  <ContentStyled>
                    <Routes>
                      <Route path={Urls.home} element={<MainTabs />} />
                    </Routes>

                    <Notifications />

                    <ApplicationErrorsModal />

                    {children}

                    <Footer />
                  </ContentStyled>
                </InnerContent>
              </Layout>
            </BrowserRouter>
          </UIProvider>
        </AuthenticatedTemplate>

        <UnauthenticatedTemplate>
          <SignIn />
        </UnauthenticatedTemplate>
      </LayoutStyled>
    </ConfigProvider>
  );
};

export default AppLayout;
